/**
 * sennagrey.com — entry point.
 *
 * Wires up the four feature modules. Each module is independently
 * importable and unit-testable; this file just orchestrates the
 * DOMContentLoaded sequence.
 *
 * Module responsibilities:
 *   - age-gate.js       18+ modal, persistent per session
 *   - reveal.js         IntersectionObserver scroll-reveal
 *   - nav.js            Active section highlighting + copyright year
 *   - newsletter.js     Provider-agnostic signup form
 *   - books.js          Renders the catalog into the #books grid
 *   - hero.js           Renders the hero name + dek/tagline from data
 */ /**
 * 18+ age-gate module.
 *
 * On first visit, the overlay covers the page. Once confirmed, the choice
 * is persisted:
 *   - localStorage if the user ticks "Remember me" (or by default on
 *     subsequent visits, see `defaultRemember`).
 *   - sessionStorage as a fallback when localStorage is unavailable
 *     (private browsing, file://, etc.).
 *
 * The body is locked from scrolling while the gate is open. A focus trap
 * keeps keyboard navigation inside the dialog.
 *
 * Public API:
 *   initAgeGate({ storage, defaultRemember } = {}) -> () => void
 *     The returned function re-opens the gate (used by the footer link).
 */ const $6ef4ad17851a13ce$var$STORAGE_KEY = 'sg_age_verified';
const $6ef4ad17851a13ce$var$SESSION_KEY = 'sg_age_verified_session';
const $6ef4ad17851a13ce$var$REMEMBER_KEY = 'sg_age_remember';
function $6ef4ad17851a13ce$var$getStorage(preferLocal) {
    try {
        const s = preferLocal ? window.localStorage : window.sessionStorage;
        const probe = '__sg_probe__';
        s.setItem(probe, '1');
        s.removeItem(probe);
        return s;
    } catch (_e) {
        return null;
    }
}
function $6ef4ad17851a13ce$var$isVerified(remember) {
    const local = $6ef4ad17851a13ce$var$getStorage(true);
    if (local && local.getItem($6ef4ad17851a13ce$var$STORAGE_KEY) === 'true' && local.getItem($6ef4ad17851a13ce$var$REMEMBER_KEY) === 'true') return true;
    const session = $6ef4ad17851a13ce$var$getStorage(false);
    if (session && session.getItem($6ef4ad17851a13ce$var$SESSION_KEY) === 'true') return true;
    // If they asked to be remembered but localStorage is unavailable, fall
    // back to a session verification so the gate doesn't re-open mid-read.
    return !remember && session !== null && session.getItem($6ef4ad17851a13ce$var$SESSION_KEY) === 'true';
}
function $6ef4ad17851a13ce$var$persistVerification(remember) {
    const local = $6ef4ad17851a13ce$var$getStorage(true);
    if (local) {
        if (remember) {
            local.setItem($6ef4ad17851a13ce$var$STORAGE_KEY, 'true');
            local.setItem($6ef4ad17851a13ce$var$REMEMBER_KEY, 'true');
        } else {
            local.removeItem($6ef4ad17851a13ce$var$STORAGE_KEY);
            local.removeItem($6ef4ad17851a13ce$var$REMEMBER_KEY);
        }
    }
    const session = $6ef4ad17851a13ce$var$getStorage(false);
    if (session) session.setItem($6ef4ad17851a13ce$var$SESSION_KEY, 'true');
}
function $6ef4ad17851a13ce$var$clearVerification() {
    const local = $6ef4ad17851a13ce$var$getStorage(true);
    if (local) {
        local.removeItem($6ef4ad17851a13ce$var$STORAGE_KEY);
        local.removeItem($6ef4ad17851a13ce$var$REMEMBER_KEY);
    }
    const session = $6ef4ad17851a13ce$var$getStorage(false);
    if (session) session.removeItem($6ef4ad17851a13ce$var$SESSION_KEY);
}
function $6ef4ad17851a13ce$var$trapFocus(container) {
    const focusables = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return ()=>{};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    function handler(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }
    container.addEventListener('keydown', handler);
    return ()=>container.removeEventListener('keydown', handler);
}
function $6ef4ad17851a13ce$var$open(gate, dialog) {
    gate.hidden = false;
    document.body.classList.add('is-locked');
    const release = $6ef4ad17851a13ce$var$trapFocus(dialog);
    const confirm = dialog.querySelector('[data-age-confirm]');
    if (confirm) confirm.focus();
    // Stash release fn so close() can call it.
    gate.dataset._release = '1';
    dialog._releaseFocus = release;
}
function $6ef4ad17851a13ce$var$close(gate, dialog) {
    gate.hidden = true;
    document.body.classList.remove('is-locked');
    if (dialog._releaseFocus) {
        dialog._releaseFocus();
        dialog._releaseFocus = null;
    }
}
function $6ef4ad17851a13ce$export$63b49cfda5a57a39(options = {}) {
    const { defaultRemember: defaultRemember = true } = options;
    const gate = document.getElementById('age-gate');
    if (!gate) return ()=>{};
    const dialog = gate.querySelector('.age-gate_card') || gate;
    const confirmBtn = gate.querySelector('[data-age-confirm]');
    const declineBtn = gate.querySelector('[data-age-decline]');
    const rememberCheckbox = gate.querySelector('[data-age-remember]');
    if (rememberCheckbox) rememberCheckbox.checked = defaultRemember;
    if ($6ef4ad17851a13ce$var$isVerified(defaultRemember)) $6ef4ad17851a13ce$var$close(gate, dialog);
    else $6ef4ad17851a13ce$var$open(gate, dialog);
    function onConfirm() {
        const remember = rememberCheckbox ? rememberCheckbox.checked : defaultRemember;
        $6ef4ad17851a13ce$var$persistVerification(remember);
        $6ef4ad17851a13ce$var$close(gate, dialog);
    }
    function onDecline() {
        // Send under-18 visitors somewhere safe.
        window.location.href = 'https://www.google.com';
    }
    if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
    if (declineBtn) declineBtn.addEventListener('click', onDecline);
    // Expose a reset hook (useful for tests, and a footer "click here if
    // you're not 18+" link can call it too).
    gate._reset = ()=>{
        $6ef4ad17851a13ce$var$clearVerification();
        $6ef4ad17851a13ce$var$open(gate, dialog);
    };
    return ()=>{
        if (gate._reset) gate._reset();
    };
}


/**
 * Scroll-reveal via IntersectionObserver.
 *
 * Elements with the `.reveal` class start hidden (CSS adds the
 * pre-reveal styles), then transition to `.is-visible` when they enter
 * the viewport. Once revealed they stay revealed (we unobserve).
 *
 * Honors `prefers-reduced-motion`: if the user has requested reduced
 * motion, everything is revealed immediately.
 */ const $3420eb5be2c64a54$var$REVEAL_ATTR = 'data-stagger-ms';
function $3420eb5be2c64a54$export$fe738788d67fcc0b() {
    const targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
        targets.forEach((el)=>el.classList.add('is-visible'));
        return;
    }
    const observer = new IntersectionObserver((entries)=>{
        entries.forEach((entry)=>{
            if (!entry.isIntersecting) return;
            const el = entry.target;
            const stagger = Number(el.getAttribute($3420eb5be2c64a54$var$REVEAL_ATTR) || 0);
            window.setTimeout(()=>el.classList.add('is-visible'), stagger);
            observer.unobserve(el);
        });
    }, {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
    });
    targets.forEach((el)=>observer.observe(el));
}


/**
 * Active-nav highlight based on which section is currently in view.
 *
 * The viewport's "center" line is used as the trigger so the active link
 * updates the moment a section occupies the middle of the screen.
 */ function $3bf65b626ad7efbf$export$ddd766f5f78a1354() {
    const sections = document.querySelectorAll('main section[id]');
    const links = document.querySelectorAll('.nav_links a[href^="#"]');
    if (!sections.length || !links.length) return;
    const observer = new IntersectionObserver((entries)=>{
        entries.forEach((entry)=>{
            if (!entry.isIntersecting) return;
            const id = entry.target.id;
            links.forEach((link)=>{
                const isMatch = link.getAttribute('href') === `#${id}`;
                link.classList.toggle('is-active', isMatch);
            });
        });
    }, {
        threshold: 0,
        rootMargin: '-50% 0px -50% 0px'
    });
    sections.forEach((s)=>observer.observe(s));
}
function $3bf65b626ad7efbf$export$a98762472d9dfc6a() {
    const year = new Date().getFullYear().toString();
    document.querySelectorAll('[data-current-year]').forEach((el)=>{
        el.textContent = year;
    });
}


/**
 * Newsletter form handler.
 *
 * Provider-agnostic. Reads its config from the `newsletter` data module
 * and:
 *   - 'mailto'       -> opens the user's mail client with a pre-filled
 *                       subject/body (default until a real provider is set up)
 *   - 'buttondown'   -> submits to https://buttondown.email/<username>
 *   - 'mailchimp'    -> posts to the configured action URL
 *   - 'convertkit'   -> posts to https://app.convertkit.com/forms/<id>/subscriptions
 *   - 'emailoctopus' -> the form markup is injected by EmailOctopus's
 *                       embed script, which sets a real `action` on the
 *                       <form> element; we let the browser submit it
 *                       natively and just report "Submitting…" status
 *
 * In every case we also render a fallback link in case the user is on
 * a browser that doesn't allow programmatic form submission, and we
 * surface a success state without reloading the page.
 */ /**
 * Centralized site content for Senna Grey.
 *
 * Keeping content in a single module — rather than scattered through the
 * HTML — makes it easy to:
 *   - keep copy in sync across pages and components
 *   - swap in real blurbs, social URLs, and bio when available
 *   - lint and unit-test the catalog (see tests/unit/content.test.js)
 *
 * Anything marked with the `// TODO(content):` prefix is a known gap
 * that requires information from Senna.
 */ const $b61b49b1012fb2d4$export$6193722c8094ee5f = {
    name: 'Senna Grey',
    /**
   * Hero is intentionally split into the small dek line above the name,
   * the (italic) lede that anchors the name, and the (roman) payoff line
   * below. Keeping each piece in data lets the page be re-skinned without
   * touching index.html.
   */ hero: {
        dek: 'Author of the Bad Professors series',
        lede: 'Steamy, character-driven romance about smart women and the dangerous men who underestimate them.',
        payoff: 'Six standalone novellas. One very compromised department.'
    },
    email: 'hello@sennagrey.com',
    domain: 'sennagrey.com',
    // TODO(content): social handles
    social: {
        amazon: 'https://www.amazon.com/stores/Senna-Grey/author/B0HB8XXGZM',
        goodreads: null,
        instagram: null,
        tiktok: null
    },
    legal: {
        copyrightHolder: 'Senna Grey',
        privacyUrl: './privacy.html'
    }
};
const $b61b49b1012fb2d4$export$b1b6e4346932dae5 = [
    {
        id: 'cute-little-tease',
        order: 1,
        title: 'Cute Little Tease',
        seriesName: 'Bad Professors',
        seriesNumber: 1,
        cover: null,
        description: "James is sure Tiffany is teasing him. Dressing to impress, doing her hair, cute high heels... all for a Thursday afternoon class? It's unheard of unless she's secretly yearning for the attention of her professor. He himself is quite smitten with the college undergrad. She's everything he looks for in a woman with her tight little figure and the clothes to show it off. He knows, however, that it can never progress beyond a daydream and he relegates himself to fantasies.",
        blurb: "James is sure Tiffany is teasing him \u2014 the outfits, the hair, the heels, all for a Thursday afternoon class. She\u2019s everything he looks for in a woman, and he knows it can never progress beyond a daydream.",
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0HB12882D'
        }
    },
    {
        id: 'fantasy-girl',
        order: 2,
        title: 'Fantasy Girl',
        seriesName: 'Bad Professors',
        seriesNumber: 2,
        cover: null,
        description: "Professor Jameson looks average from the outside: normal hobbies, average car, regular apartment. He teaches mathematics at the local college. He's a normal, average, regular, maybe even boring guy. Until he isn't. Chelsea is a student in his calculus class that he just can't get out of his head. She's occupying his mind day and night and has been for nearly a year. While his life may look boring to outsiders, his inner life is full of passion and desire.",
        blurb: 'Professor Jameson looks average from the outside \u2014 until Chelsea, the calculus student he can\u2019t get out of his head, occupies his mind day and night.',
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0HFDLTWGX'
        }
    },
    {
        id: 'confessions',
        order: 3,
        title: 'Confessions',
        seriesName: 'Bad Professors',
        seriesNumber: 3,
        cover: null,
        description: "Mitchell is a good guy. He has a good job as a college professor and gets up and goes to work every day for the love of his life, his wife Mindy. The only problem? His mind has been elsewhere for months. He sometimes feels like he's living a second life.",
        blurb: 'Mitchell is a good guy: a good job, a wife he loves. The only problem? His mind has been elsewhere for months.',
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0HCQ3YM6V'
        }
    },
    {
        id: 'ride-home',
        order: 4,
        title: 'Ride Home',
        seriesName: 'Bad Professors',
        seriesNumber: 4,
        cover: null,
        description: 'College professor Jack Kent has a pair of secrets. Two students in his university English course are on his mind all the time. He finds himself daydreaming about them constantly but knows that nothing can ever come of his endless pining. He has resigned himself to fantasies.',
        blurb: 'College professor Jack Kent has a pair of secrets \u2014 two students in his English course he can\u2019t stop daydreaming about.',
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0HCMGJPL1'
        }
    },
    {
        id: 'after-office-hours',
        order: 5,
        title: 'After Office Hours',
        seriesName: 'Bad Professors',
        seriesNumber: 5,
        cover: null,
        description: "John's office hours have been taken over by his college calculus student, Angie. He tries to play it cool but the heat is becoming unbearable between her playful smiles and lingering hands. She's clearly more interested in John beyond a normal professor-student relationship. But what about John's wife, Sarah? He's resorted to taking out his sexual frustrations on her every Thursday after office hours spent with Angie but he just can't shake the college girl out of his mind.",
        blurb: 'John\u2019s office hours have been taken over by his calculus student, Angie. The heat is unbearable \u2014 but what about John\u2019s wife, Sarah?',
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0H8ZJHGY6'
        }
    },
    {
        id: 'hot-threesomes',
        order: 6,
        title: 'Hot Threesomes: Bad Professors Collection',
        seriesName: 'Bad Professors',
        seriesNumber: null,
        cover: null,
        description: 'A Bad Professors series collection. Titles include "After Office Hours", "Ride Home" and "Confessions". A trio of hot threesome stories to tantalize and excite. Meet three college professors who all have one thing in common\u2014they\u2019ve got a student on their mind. What sort of sultry escapades will they get up to? More important, what will happen when a third joins in?',
        blurb: 'A Bad Professors collection: "After Office Hours", "Ride Home", and "Confessions" \u2014 three hot threesome stories to tantalize and excite.',
        retailer: {
            amazon: 'https://www.amazon.com/dp/B0HDMPL7GD'
        }
    }
];
const $b61b49b1012fb2d4$export$7589597339a0562d = {
    name: 'Bad Professors',
    tagline: 'Standalone novellas. Read in any order.',
    amazon: 'https://www.amazon.com/dp/B0H9RMFTXR'
};
const $b61b49b1012fb2d4$export$e86d578d3f4f42b6 = {
    short: 'Senna Grey writes steamy, character-driven romance about smart women and the dangerous men who underestimate them. Her Bad Professors series has been keeping readers up way past their bedtime since 2024.',
    // TODO(content): longer bio paragraph for the press section
    long: null
};
const $b61b49b1012fb2d4$export$d1bae722e5813031 = {
    provider: 'emailoctopus',
    emailoctopus: {
        formId: '197c0c3a-a586-11f1-89ad-c716a93f1566'
    },
    // kept around as a reference for future provider swaps
    // buttondown example: { username: 'sennagrey' }
    buttondown: {
        username: null
    },
    mailchimp: {
        actionUrl: null,
        hiddenFieldName: 'b_HASH'
    },
    convertkit: {
        formId: null
    }
};


function $a0bb2cc150714df5$var$appendFallback(form, message) {
    const fallback = document.querySelector('.newsletter_fallback');
    if (!fallback) return;
    if (message) fallback.textContent = message;
    fallback.hidden = false;
    form.hidden = true;
}
function $a0bb2cc150714df5$var$setStatus(form, message, state) {
    const status = form.querySelector('.newsletter_status');
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state || '';
}
function $a0bb2cc150714df5$export$16cf041c316b2823() {
    const form = document.querySelector('[data-newsletter-form]');
    if (!form) return;
    // If the form already has a real action attribute (and isn't the
    // mailto: pseudo-action), let the browser submit it natively.
    const action = form.getAttribute('action') || '';
    if (action && !action.startsWith('mailto:') && (0, $b61b49b1012fb2d4$export$d1bae722e5813031).provider !== 'mailto') {
        form.addEventListener('submit', ()=>{
            $a0bb2cc150714df5$var$setStatus(form, 'Submitting\u2026', 'pending');
        });
        return;
    }
    form.addEventListener('submit', (e)=>{
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        const email = input ? input.value.trim() : '';
        if (!email) return;
        if ((0, $b61b49b1012fb2d4$export$d1bae722e5813031).provider === 'mailto') {
            const subject = encodeURIComponent('Newsletter signup');
            const body = encodeURIComponent(`Please add ${email} to the Senna Grey newsletter.`);
            window.location.href = `mailto:${'hello@sennagrey.com'}?subject=${subject}&body=${body}`;
            $a0bb2cc150714df5$var$appendFallback(form, `If your mail app didn't open, email hello@sennagrey.com and we'll add you when the newsletter is ready.`);
            return;
        }
        // For real providers, fall through to a programmatic submit.
        form.submit();
    });
}


/**
 * Book-card renderer.
 *
 * Takes the catalog from the data module and populates a <ul> with one
 * <li class="book_card"> per book. Keeps the markup out of the static
 * HTML so adding a new title is a one-line change to the data file.
 */ 
/** Inline ornament, kept tiny and reused across placeholders. */ function $fa9e2603a017443d$var$ornamentSvg() {
    return `
    <svg class="book_cover_ornament" viewBox="0 0 60 8" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <line x1="0" y1="4" x2="22" y2="4" stroke="currentColor" stroke-width="0.8"/>
      <line x1="38" y1="4" x2="60" y2="4" stroke="currentColor" stroke-width="0.8"/>
      <path d="M 30 0.5 L 33 4 L 30 7.5 L 27 4 Z" fill="currentColor"/>
    </svg>`;
}
function $fa9e2603a017443d$var$coverHtml(book) {
    if (book.cover) return `<img class="book_cover" src="${book.cover}" alt="Cover of ${book.title}" loading="lazy">`;
    // Decorative placeholder when no cover is provided yet. Mimics a real
    // book spine: tiny series label on top, ornament, then the title set
    // in the display serif.
    return `
    <div class="book_cover book_cover--placeholder" aria-hidden="true">
      <span class="book_cover_label">${book.seriesName}</span>
      ${$fa9e2603a017443d$var$ornamentSvg()}
      <span class="book_cover_glyph">${book.title}</span>
    </div>`;
}
function $fa9e2603a017443d$var$retailersHtml(book) {
    const links = [];
    if (book.retailer.amazon) links.push(`<a class="btn btn-small" href="${book.retailer.amazon}" target="_blank" rel="noopener">Read &ldquo;${book.title}&rdquo; on Amazon</a>`);
    return links.join('');
}
function $fa9e2603a017443d$var$cardHtml(book) {
    const seriesLabel = book.seriesNumber ? `${book.seriesName}, Book ${book.seriesNumber}` : `${book.seriesName} \u2014 Collection`;
    return `
    <li class="book_card reveal" data-book-id="${book.id}">
      <div class="book_cover_wrap">
        ${$fa9e2603a017443d$var$coverHtml(book)}
      </div>
      <div class="book_meta">
        <p class="book_series">${seriesLabel}</p>
        <h3 class="book_title">${book.title}</h3>
        <p class="book_blurb">${book.description || book.blurb}</p>
        <div class="book_actions">${$fa9e2603a017443d$var$retailersHtml(book)}</div>
      </div>
    </li>`;
}
function $fa9e2603a017443d$export$4474b306a806f2d4(target = '#books .book_grid') {
    const grid = document.querySelector(target);
    if (!grid) return;
    const ordered = [
        ...(0, $b61b49b1012fb2d4$export$b1b6e4346932dae5)
    ].sort((a, b)=>a.order - b.order);
    grid.innerHTML = ordered.map($fa9e2603a017443d$var$cardHtml).join('');
}
function $fa9e2603a017443d$export$7bd7f4f4ee2bb13d(target = '#books .series_link') {
    const link = document.querySelector(target);
    if (!link) return;
    link.setAttribute('href', (0, $b61b49b1012fb2d4$export$7589597339a0562d).amazon);
}


/**
 * Hero renderer.
 *
 * Pulls the name + dek/tagline from the content module and populates
 * the corresponding elements in the static markup. The name is split
 * on whitespace into stacked lines (e.g. "Senna Grey" -> "Senna" /
 * "Grey") so it can be set in the largest possible size without
 * forcing an awkward break.
 *
 * The hero h1 still contains the full name as text in the markup for
 * SEO and accessibility — this function just wraps each whitespace
 * token in a <span> for styling, leaving the underlying text intact.
 */ 
function $980ce613df053a34$var$splitNameIntoLines(name) {
    // Split on whitespace, drop empties, wrap each in a span.
    return name.split(/\s+/).filter(Boolean).map((token)=>`<span class="hero_name-line">${token}</span>`).join('');
}
function $980ce613df053a34$var$applyTagline(target, lede, payoff) {
    if (!target) return;
    // Lede is rendered in italic; payoff in roman. The em/em-marked
    // first sentence is the italic part.
    target.innerHTML = `<em>${lede}</em> ${payoff}`;
}
function $980ce613df053a34$export$edcabd32f4cb34d2() {
    const nameTarget = document.querySelector('#hero .hero_name');
    if (nameTarget) nameTarget.innerHTML = $980ce613df053a34$var$splitNameIntoLines((0, $b61b49b1012fb2d4$export$6193722c8094ee5f).name);
    const dekTarget = document.querySelector('#hero .hero_dek');
    if (dekTarget && (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero?.dek) dekTarget.textContent = (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero.dek;
    const taglineTarget = document.querySelector('#hero .hero_tagline');
    if (taglineTarget && (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero?.lede && (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero?.payoff) $980ce613df053a34$var$applyTagline(taglineTarget, (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero.lede, (0, $b61b49b1012fb2d4$export$6193722c8094ee5f).hero.payoff);
}


function $b99a74a38422fa3f$var$ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
}
$b99a74a38422fa3f$var$ready(()=>{
    // Render dynamic content first so .reveal elements exist when the
    // observer scans the DOM.
    (0, $980ce613df053a34$export$edcabd32f4cb34d2)();
    (0, $fa9e2603a017443d$export$4474b306a806f2d4)();
    (0, $fa9e2603a017443d$export$7bd7f4f4ee2bb13d)();
    const reopenAgeGate = (0, $6ef4ad17851a13ce$export$63b49cfda5a57a39)({
        defaultRemember: true
    });
    // Footer link: "Click here if you need to re-confirm."
    document.querySelectorAll('[data-age-reset]').forEach((el)=>{
        el.addEventListener('click', (e)=>{
            e.preventDefault();
            reopenAgeGate();
        });
    });
    (0, $3bf65b626ad7efbf$export$a98762472d9dfc6a)();
    (0, $3420eb5be2c64a54$export$fe738788d67fcc0b)();
    (0, $3bf65b626ad7efbf$export$ddd766f5f78a1354)();
    (0, $a0bb2cc150714df5$export$16cf041c316b2823)();
});


//# sourceMappingURL=repo.9d2116b3.js.map
